import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiPageResponse, PageQuery } from '../types/api.types';
import { Invoice, MarkInvoicePaidRequest } from '../types/invoice.types';
import { PaymentTransactionView } from '../types/transaction.types';

@Injectable({ providedIn: 'root' })
export class InvoicesService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/rental/api/invoices`;

  list(query: PageQuery = {}): Observable<ApiPageResponse<Invoice>> {
    return this.http.get<ApiPageResponse<Invoice>>(this.base, {
      params: this.pageParams(query),
    });
  }

  /** Self-service : factures du user courant (locataire OU propriétaire) — paginé. */
  listMine(opts: { page?: number; size?: number } = {}): Observable<ApiPageResponse<Invoice>> {
    const params = new HttpParams()
      .set('page', String(opts.page ?? 1))
      .set('size', String(opts.size ?? 10));
    return this.http.get<ApiPageResponse<Invoice>>(`${this.base}/me`, { params });
  }

  /** Factures en retard du user courant — paginé. */
  listMineOverdue(opts: { page?: number; size?: number } = {}): Observable<ApiPageResponse<Invoice>> {
    const params = new HttpParams()
      .set('page', String(opts.page ?? 1))
      .set('size', String(opts.size ?? 10));
    return this.http.get<ApiPageResponse<Invoice>>(`${this.base}/me/overdue`, { params });
  }

  get(id: number): Observable<Invoice> {
    return this.http.get<Invoice>(`${this.base}/${id}`);
  }

  /** Détail self-service (back vérifie que la facture appartient au user). */
  getMine(id: number): Observable<Invoice> {
    return this.http.get<Invoice>(`${this.base}/me/${id}`);
  }

  getByRental(rentalId: number): Observable<Invoice> {
    return this.http.get<Invoice>(`${this.base}/rental/${rentalId}`);
  }

  /**
   * Transactions de paiement de la facture (§2.8.bis) — **tableau brut** de
   * `PaymentTransactionView`, relayé par rental (ownership locataire/loueur
   * vérifié, puis S2S vers payment). Remplace `by-source`, réservé ROLE_ADMIN
   * → 403 pour un owner.
   */
  transactions(id: number): Observable<PaymentTransactionView[]> {
    return this.http.get<PaymentTransactionView[]>(`${this.base}/${id}/transactions`);
  }

  /**
   * URL du PDF, avec **régénération paresseuse** côté back (2026-07-16) : la
   * génération à la création est best-effort, une facture peut donc exister
   * sans PDF — cet appel le régénère (~1-2 s au premier appel) puis renvoie
   * l'URL. 404 seulement si la régénération échoue vraiment.
   *
   * ⚠️ Réponse en **texte brut** (l'URL nue, pas du JSON) → `responseType: 'text'`.
   * Self-scoped (`findOneMe` : locataire/loueur — un admin tiers ne peut pas).
   */
  download(id: number): Observable<string> {
    return this.http.get(`${this.base}/${id}/download`, { responseType: 'text' });
  }

  /**
   * **Owner** — atteste l'encaissement en **espèces** au retrait :
   * `POST /rental/api/invoices/{id}/confirm-cash?paymentReference=`. Owner-scoped
   * (403 si le caller n'est pas le loueur du véhicule). La facture passe `PAID` en
   * mode CASH (commission débitée du CREDIT prestataire, aucun crédit BALANCE).
   * ⚠️ Ne PAS confondre avec `markAsPaid` (mode DIGITAL, admin).
   */
  confirmCash(id: number, paymentReference?: string): Observable<Invoice> {
    let params = new HttpParams();
    if (paymentReference?.trim()) params = params.set('paymentReference', paymentReference.trim());
    return this.http.post<Invoice>(`${this.base}/${id}/confirm-cash`, null, { params });
  }

  /** Admin/Owner action — marks an invoice as paid, returns the updated entity. */
  markAsPaid(id: number, payload: MarkInvoicePaidRequest): Observable<Invoice> {
    let params = new HttpParams().set('paymentMethod', payload.paymentMethod);
    if (payload.paymentReference?.trim()) {
      params = params.set('paymentReference', payload.paymentReference.trim());
    }
    return this.http.post<Invoice>(`${this.base}/${id}/mark-paid`, null, { params });
  }

  cancel(id: number, reason?: string): Observable<Invoice> {
    let params = new HttpParams();
    if (reason?.trim()) params = params.set('reason', reason.trim());
    return this.http.post<Invoice>(`${this.base}/${id}/cancel`, null, { params });
  }

  /** Batch — refresh OVERDUE status on all past-due invoices. */
  updateOverdue(): Observable<string> {
    return this.http.post(`${this.base}/update-overdue`, null, {
      responseType: 'text',
    });
  }

  count(search?: string): Observable<number> {
    let params = new HttpParams();
    if (search?.trim()) params = params.set('search', search.trim());
    return this.http.get<number>(`${this.base}/count`, { params });
  }

  private pageParams(query: PageQuery): HttpParams {
    let params = new HttpParams()
      .set('pageable', 'true')
      .set('page', String(query.page ?? 1))
      .set('size', String(query.size ?? 10))
      .set('isDeleted', String(query.isDeleted ?? false));
    if (query.search?.trim()) params = params.set('search', query.search.trim());
    return params;
  }
}
